<template>
  <div class="value-mapper">
    <div class="mapper-header">
      <h3>{{ title }}</h3>
      <p class="description">
        Your CSV contains <strong>{{ uniqueValues.length }}</strong> unique {{ entityLabel }} value(s).
        Map each to a DriveScout {{ entityLabel }}:
      </p>
    </div>

    <div class="mapping-table-wrapper">
      <table class="mapping-table">
        <thead>
          <tr>
            <th>CSV Value</th>
            <th>Rows</th>
            <th>Maps To</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in uniqueValues" :key="item.value">
            <td class="csv-value">
              <code>{{ item.value }}</code>
            </td>
            <td class="row-count">
              {{ item.count }}
            </td>
            <td class="mapping-select">
              <select
                :value="localMapping[item.value]"
                @change="updateMapping(item.value, $event.target.value)"
                :class="{ 'unmapped': localMapping[item.value] === undefined }"
              >
                <option :value="undefined" disabled>-- Select --</option>
                <option value="skip">Skip - do not import these rows</option>
                <optgroup :label="optionGroupLabel">
                  <option
                    v-for="option in availableOptions"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </optgroup>
              </select>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mapping-summary" v-if="uniqueValues.length > 0">
      <div class="summary-item" :class="{ 'complete': allMapped }">
        <span v-if="allMapped">All values mapped</span>
        <span v-else>{{ mappedCount }} of {{ uniqueValues.length }} values mapped</span>
      </div>
      <div class="summary-item skip-count" v-if="skipCount > 0">
        <span>{{ skipCount }} value(s) will be skipped ({{ skipRowCount }} rows)</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CourseValueMapper',

  props: {
    title: {
      type: String,
      default: 'Map Values'
    },
    entityLabel: {
      type: String,
      default: 'value'
    },
    optionGroupLabel: {
      type: String,
      default: 'Options'
    },
    uniqueValues: {
      type: Array,
      required: true,
      // Expected format: [{ value: string, count: number }]
    },
    availableOptions: {
      type: Array,
      required: true,
      // Expected format: [{ id: number, name: string }]
    },
    mapping: {
      type: Object,
      required: true,
      // Format: { "CSV Value": entity_id | "skip" }
    },
  },

  data() {
    return {
      localMapping: { ...this.mapping },
    };
  },

  computed: {
    allMapped() {
      return this.uniqueValues.every(
        (item) => this.localMapping[item.value] !== undefined
      );
    },

    mappedCount() {
      return this.uniqueValues.filter(
        (item) => this.localMapping[item.value] !== undefined
      ).length;
    },

    skipCount() {
      return this.uniqueValues.filter(
        (item) => this.localMapping[item.value] === 'skip'
      ).length;
    },

    skipRowCount() {
      return this.uniqueValues
        .filter((item) => this.localMapping[item.value] === 'skip')
        .reduce((sum, item) => sum + item.count, 0);
    },
  },

  watch: {
    mapping: {
      handler(newMapping) {
        this.localMapping = { ...newMapping };
      },
      deep: true,
    },
  },

  methods: {
    updateMapping(csvValue, selectedValue) {
      // Convert "skip" to the string, otherwise parse as integer
      let mappedValue;
      if (selectedValue === 'skip') {
        mappedValue = 'skip';
      } else if (selectedValue === undefined || selectedValue === '') {
        mappedValue = undefined;
      } else {
        mappedValue = parseInt(selectedValue, 10);
      }

      this.localMapping = {
        ...this.localMapping,
        [csvValue]: mappedValue,
      };

      this.$emit('update:mapping', this.localMapping);
    },
  },
};
</script>

<style scoped>
.value-mapper {
  margin-bottom: 24px;
}

.mapper-header {
  margin-bottom: 16px;
}

.mapper-header h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.mapper-header .description {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.mapping-table-wrapper {
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.mapping-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.mapping-table th,
.mapping-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.mapping-table th {
  background-color: #f5f5f5;
  font-weight: 600;
  color: #333;
}

.mapping-table tr:last-child td {
  border-bottom: none;
}

.mapping-table tr:hover {
  background-color: #fafafa;
}

.csv-value code {
  background-color: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: monospace;
  font-size: 13px;
}

.row-count {
  color: #666;
  font-weight: 500;
  text-align: center;
  min-width: 60px;
}

.mapping-select select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  cursor: pointer;
}

.mapping-select select:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.mapping-select select.unmapped {
  border-color: #ff9800;
  background-color: #fff8e1;
}

.mapping-summary {
  margin-top: 16px;
  display: flex;
  gap: 16px;
  font-size: 14px;
}

.summary-item {
  padding: 8px 12px;
  background-color: #fff8e1;
  border-radius: 4px;
  color: #f57c00;
}

.summary-item.complete {
  background-color: #e8f5e9;
  color: #4caf50;
}

.summary-item.skip-count {
  background-color: #fce4ec;
  color: #e91e63;
}
</style>
